/**
 * Labstep
 *
 * @module grid/NewIndex/coldefs/entity/entity-user-role
 * @desc Column Definition for entity user with a specific role
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { EntityUserActionManage } from 'labstep-web/components/EntityUser/Action/Manage';
import { EntityUserActionManageList } from 'labstep-web/components/EntityUser/Action/Manage/List';
import { ICONS } from 'labstep-web/constants/icons';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import type {
  CellEditorParams,
  CellRendererParams,
} from 'labstep-web/core/DataGrid/types';
import { stopEditingAndFocus } from 'labstep-web/core/DataGrid/utils';
import type { NewIndexColDef } from 'labstep-web/grid/NewIndex/coldefs/types';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import { PostFilterFieldTypes } from 'labstep-web/grid/services/grid-filter.service';
import type {
  EntityUser,
  EntityUserEntities,
} from 'labstep-web/models/entity-user.model';
import { User } from 'labstep-web/models/user.model';

export const getColDefEntityEntityUsersEntityUserRole = (
  entityUserRoleName: string,
): NewIndexColDef => ({
  colId: `entity_users_entity_user_role:${entityUserRoleName}`,
  headerName: entityUserRoleName,
  headerComponentParams: {
    icon: ICONS.user.secondary,
    postFilterSettings: {
      fieldType: PostFilterFieldTypes.entity,
      path: 'entityUsers.user',
      field: 'guid',
      entityName: User.entityName,
      extraParams: {
        entity_user_role_name: entityUserRoleName,
      },
    },
  },
  editable: (params: EditableCallbackParams) =>
    GridNewIndexService.getEditable(params),
  cellStyle: { display: 'flex', alignItems: 'center' },
  valueGetter: (params: ValueGetterParams): string =>
    params.node?.data && params.node?.data?.entity_users_preview
      ? params.node?.data?.entity_users_preview
          .filter(
            (entityUser: EntityUser) =>
              entityUser.entity_user_role &&
              entityUser.entity_user_role.name === entityUserRoleName,
          )
          .map(
            (entityUser: EntityUser) => entityUser.user?.name || '',
          )
          .join(', ')
      : '',
  valueGetterExport: (params: ValueGetterParams) =>
    params.node?.data && params.node?.data?.entity_users_preview
      ? params.node?.data?.entity_users_preview
          .filter(
            (entityUser: EntityUser) =>
              entityUser.entity_user_role &&
              entityUser.entity_user_role.name === entityUserRoleName,
          )
          .map(
            (entityUser: EntityUser) => entityUser.user?.name || '',
          )
          .join(', ')
      : '',
  cellRenderer: (params: CellRendererParams<EntityUserEntities>) => {
    if (!params.data) {
      return '';
    }
    return (
      <ParamsStateContextProvider value={{}}>
        <EntityUserActionManageList
          entity={params.data}
          entityUserRoleName={entityUserRoleName}
        />
      </ParamsStateContextProvider>
    );
  },
  cellEditorPopup: true,
  cellEditor: (params: CellEditorParams<EntityUserEntities>) => {
    const { data } = params;
    return (
      <ParamsStateContextProvider value={{}}>
        <EntityUserActionManage
          entity={data}
          viewComponent={null}
          open
          onClose={(): void => stopEditingAndFocus(params)}
        />
      </ParamsStateContextProvider>
    );
  },
});
